/* eslint-disable @typescript-eslint/no-explicit-any */
import { JSXElementConstructor, ReactElement, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { optionalProp } from '../../core';
import { PrimaryButtonLabel, SelectorType, TimeMeridiem, TimePickerModal } from '../types/enums';
import { Data } from '../../DropDown/types/propsType';
import { type CalendarDaysMappingInterface, DAY_LIMIT } from './../types/propTypes';
import styles from './Calendar.module.scss';

const monthsInAYear = 12;

export const splitArrayInGroups = (arr: React.ReactElement[], size: number) => {
  const result: { id: number; name: ReactElement<any, string | JSXElementConstructor<any>>[] }[] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push({ id: i, name: arr.slice(i, i + size) });
  }
  return result;
};

export const getTimeList = (
  intervalInMins = 15,
  hours12 = true,
  startingHour = 0,
  disablePastTime = false,
  selectedDate?: Date,
) => {
  const timeList: [hours: number, interval: number][] = [];
  const currentDate = new Date();

  for (let hour = 0; hour < 24; hour++) {
    for (let interval = 0; interval < 60; interval += intervalInMins) {
      timeList.push([(hour + startingHour) % 24, interval]);
    }
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: hours12,
  });

  const slotDate = selectedDate ? new Date(selectedDate) : new Date(currentDate);
  const isToday =
    slotDate.getDate() === currentDate.getDate() &&
    slotDate.getMonth() === currentDate.getMonth() &&
    slotDate.getFullYear() === currentDate.getFullYear();

  return timeList.map(([hour, minute]: any) => {
    const slot = new Date(slotDate);
    slot.setHours(hour, minute, 0, 0);
    const formattedTime = formatter.format(slot);

    const nowMinutes = currentDate.getHours() * 60 + currentDate.getMinutes();
    const slotMinutes = hour * 60 + minute;
    const isTimeDisabled = isToday && disablePastTime && slotMinutes < nowMinutes;

    return { id: formattedTime, name: formattedTime, isDisabled: isTimeDisabled };
  });
};

export const getTimeDataList = (is24HoursFormat: boolean) => {
  const timeList: { id: string; name: string }[] = [];
  let initial = 0,
    limit = 12;
  if (is24HoursFormat) {
    initial = 0;
    limit = 24;
  }
  for (let i = initial; i < limit; i++) {
    const hour = `${String(!is24HoursFormat && i === 0 ? limit : i).padStart(2, '0')}:00`;
    timeList.push({ id: hour, name: hour });
  }
  return timeList;
};
const getDateTime = (yyyy: number, mm: number, dd: number | undefined) => new Date(yyyy, mm, dd).getTime();

export const getDateTimeDetails = (date: Date) => getDateTime(date.getFullYear(), date.getMonth(), date.getDate());

export const newYearDirection = (direction: 'left' | 'right', year: number, month: number) =>
  direction === 'left' ? year - (month === 0 ? 1 : 0) : year + (month === 11 ? 1 : 0);

export const newMonthDirection = (direction: 'left' | 'right', month: number) =>
  (month + (direction === 'left' ? -1 : 1) + monthsInAYear) % monthsInAYear;

export const calendarTitleStyling = (calendarTitle: string) =>
  calendarTitle ? styles['show-calendar-title-only'] : '';

export const selectedRangeStyling = (selectRange: boolean) => (selectRange ? styles['date-row-range'] : '');

export const selectedRangeLabel = (selectRange: boolean, timeLabelStart: string, timeLabel: string) =>
  selectRange ? timeLabelStart : timeLabel;

// Calendar week mapping function
export const isDateDisabled = (
  currentDate: Date,
  disabledDates: Date[] | undefined,
  disabledPastDates: boolean | undefined,
  show4WeeksOnly: boolean,
  disableDate?: (e: Date) => boolean,
): boolean => {
  const todayDate = new Date();
  todayDate.setHours(0, 0, 0, 0);
  const currentDateTime = getDateTimeDetails(currentDate);
  if (disableDate && disableDate(currentDate)) {
    return true;
  }
  const isPastDateDisabled = () => {
    if (disabledPastDates) {
      return currentDateTime < getDateTimeDetails(todayDate);
    }
    return false;
  };
  if (show4WeeksOnly && currentDateTime > getDateTime(currentDate.getFullYear(), currentDate.getMonth(), DAY_LIMIT)) {
    return true;
  }
  return !!(disabledDates?.find((disabledDate) => disabledDate.getTime() === currentDateTime) || isPastDateDisabled());
};
export const getDateInfo = (show4WeeksOnly: boolean, date?: Date, defaultYear?: number) => {
  const baseDate = new Date();
  if (defaultYear) {
    baseDate.setFullYear(defaultYear);
  }
  const localDate = date || baseDate;
  if (show4WeeksOnly) {
    localDate.setMonth(0);
  }
  const year = localDate.getFullYear();
  const month = localDate.getMonth();
  const hour = localDate.getHours();
  const minute = localDate.getMinutes();
  return {
    year: year,
    month: month,
    date: localDate,
    monthFirstDay: new Date(year, month, 1),
    monthFirstWeekDay: new Date(year, month, 1).getDay(),
    totalMonthDays: new Date(year, month + 1, 0).getDate(),
    hour,
    minute,
  };
};
export const getEventOnDate = (currentDate: Date, scheduleEventOnDates?: Date[]): ReactNode => {
  const scheduledDate = scheduleEventOnDates
    ? scheduleEventOnDates.find((schedule) => getDateTimeDetails(currentDate) === getDateTimeDetails(schedule))
    : false;
  return scheduledDate ? (
    <div className={styles['scheduled-event-date']} data-testid="week-span-event-testid" />
  ) : (
    <></>
  );
};
const isSameDay = (d1: Date, d2: Date) => d1.toDateString() === d2.toDateString();
const isWithinRange = (start: Date, middle: Date, end: Date) =>
  getDateTimeDetails(start) < getDateTimeDetails(middle) && getDateTimeDetails(middle) < getDateTimeDetails(end);
const computeDateWrapperCls = (
  baseDate: Date | undefined,
  baseEndDate: Date | undefined,
  currentDate: Date,
  intermediateEndDate: Date | undefined,
  currentDate_weekDay: number,
  show4WeeksOnly: boolean,
  selectRange: boolean,
  disabledPastDates?: boolean,
  disabledDates?: Date[],
  disableDate?: (e: Date) => boolean,
) => {
  const selectionState = baseDate ? isSameDay(currentDate, baseDate) : false;
  let selectionToState = false,
    isWithinSelectedRange = false,
    rangeCellCls = '';
  const isDisabled = isDateDisabled(currentDate, disabledDates, disabledPastDates, show4WeeksOnly, disableDate);
  const currentEndDate = baseEndDate || intermediateEndDate;
  if (selectRange && baseDate && currentEndDate) {
    selectionToState = isSameDay(currentDate, currentEndDate);
    isWithinSelectedRange = isWithinRange(baseDate, currentDate, currentEndDate);
    if (selectionState && getDateTimeDetails(baseDate) < getDateTimeDetails(currentEndDate)) {
      rangeCellCls = styles['selected-cell-from'];
    } else if (selectionToState) {
      rangeCellCls = styles['selected-cell-to'];
    }
    if (isWithinSelectedRange) {
      rangeCellCls = styles['selected-range-cell'];
      if (currentDate_weekDay === 0) {
        rangeCellCls += ` ${styles['range-left']}`;
      } else if (currentDate_weekDay === 6) {
        rangeCellCls += ` ${styles['range-right']}`;
      }
    }
  }
  return {
    selectionState,
    selectionToState,
    isWithinSelectedRange,
    rangeCellCls,
    isDisabled,
  };
};
const getWrapperBaseCellCls = (isCurrentMonth: boolean, isDisabled: boolean, selected: boolean, rangeCellCls: string) =>
  `${styles['base-date-cell']} ${isCurrentMonth && styles['selectable-cell']} ${
    isDisabled ? styles['disabled-cell'] : ''
  } ${selected ? styles['selected-cell'] : ''} ${rangeCellCls}`;

export const calendarDaysMapping = ({
  baseDate,
  baseEndDate,
  intermediateDate,
  intermediateEndDate,
  showFullCalendar,
  show4WeeksOnly,
  show31DayCalendar,
  handleDateTimeUpdate,
  selectRange,
  scheduleEventOnDates,
  disableDate,
  disabledDates,
  disabledPastDates,
  ariaLabel,
  defaultYear,
}: CalendarDaysMappingInterface) => {
  const {
    month,
    date,
    monthFirstDay,
    monthFirstWeekDay,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    totalMonthDays: total_month_days,
  } = getDateInfo(show4WeeksOnly, intermediateDate, defaultYear);

  const currentDate = monthFirstDay;
  const currentDateWeekDay = monthFirstWeekDay;
  const currentWeekDays: JSX.Element[] = [];

  const rowsOfDates = () =>
    showFullCalendar || show31DayCalendar ? Math.ceil((total_month_days + monthFirstWeekDay) / 7) : 5;

  const weekGrid = rowsOfDates() * 7;
  let intermediateWeekSpan: JSX.Element[] = [];

  for (let day = 0; day < weekGrid; day++) {
    const nextDate = currentDate.getDate() + 1;
    if (day === 0 && currentDateWeekDay === 0) {
      // currentDate.setDate(currentDate.getDate() - 7);
    } else if (day === 0) {
      currentDate.setDate(currentDate.getDate() + (day - currentDateWeekDay));
    } else if (currentDate.getMonth() === month) {
      if (nextDate <= total_month_days) {
        currentDate.setDate(nextDate);
      } else {
        currentDate.setMonth(month + 1);
        currentDate.setDate(nextDate - total_month_days);
      }
    } else {
      currentDate.setDate(nextDate);
    }
    // current date info
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const currentDate_weekDay = currentDate.getDay();

    // ui state checks
    const { selectionState, selectionToState, isWithinSelectedRange, rangeCellCls, isDisabled } = computeDateWrapperCls(
      baseDate,
      baseEndDate,
      currentDate,
      intermediateEndDate,
      currentDate_weekDay,
      show4WeeksOnly,
      selectRange,
      disabledPastDates,
      disabledDates,
      disableDate,
    );

    const calendarDay = {
      isCurrentMonth: currentDate.getMonth() === date.getMonth(),
      date: new Date(currentDate),
      month: currentDate.getMonth(),
      number: currentDate.getDate(),
      selected: selectionState || selectionToState,
      year: currentDate.getFullYear(),
      isWithinSelectedRange: isWithinSelectedRange,
    };

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const day_cell = (
      <button
        type="button"
        key={calendarDay.date.toISOString()}
        className={getWrapperBaseCellCls(calendarDay.isCurrentMonth, isDisabled, calendarDay.selected, rangeCellCls)}
        aria-label={ariaLabel}
        {...(!isDisabled
          ? {
              onClick: (e) => handleDateTimeUpdate(e, new Date(calendarDay.date), false),
              onMouseEnter: (e) => handleDateTimeUpdate(e, new Date(calendarDay.date), true),
            }
          : {})}
        tabIndex={0}
      >
        <span className={styles['date-cell']} data-testid="week-span-testid">
          {calendarDay.number}
          {getEventOnDate(calendarDay.date, scheduleEventOnDates)}
        </span>
      </button>
    );
    intermediateWeekSpan.push(day_cell);
    if (intermediateWeekSpan.length === 7) {
      currentWeekDays.push(<div className={styles['week-day-sequence']}>{intermediateWeekSpan}</div>);
      intermediateWeekSpan = [];
    }
  }
  return currentWeekDays;
};
export const getPrimaryBtnProp = (
  value: Date | undefined,
  calendarModalDateTime: Date | undefined,
  valueTo: Date | undefined,
  calendarModalEndDateTime: Date | undefined,
  startTimeValue: string | undefined,
  startTime: string | undefined,
  endTimeValue: string | undefined,
  endTime: string | undefined,
  selectRange: boolean,
  showTime: boolean,
  handlePrimaryBtnClick: (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void,
  handleDateTimeClear: (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void,
  currentPicker: string,
  scheduleLabel?: string,
) => {
  const returnObj = {
    btnTitle: scheduleLabel || PrimaryButtonLabel.APPLY,
    btnCB: handlePrimaryBtnClick,
    isDisable: true,
  };
  // when timepicker is open
  if (currentPicker !== TimePickerModal.NONE) {
    returnObj.isDisable = false;
    return returnObj;
  }

  if (selectRange) {
    if (!calendarModalEndDateTime) {
      return returnObj;
    } else if (showTime && !(endTime && startTime)) {
      return returnObj;
    } else if (calendarModalEndDateTime && calendarModalEndDateTime !== valueTo) {
      returnObj.isDisable = false;
      return returnObj;
    }
  }

  if (!calendarModalDateTime) {
    return returnObj;
  } else if (showTime && !startTime) {
    return returnObj;
  } else if (calendarModalDateTime && calendarModalDateTime !== value) {
    returnObj.isDisable = false;
    return returnObj;
  }

  return {
    btnTitle: scheduleLabel || PrimaryButtonLabel.CLEAR,
    btnCB: scheduleLabel ? handlePrimaryBtnClick : handleDateTimeClear,
    isDisable: false,
  };
};
export const getModifiedTimeInDate = (
  is24HoursFormat: boolean,
  dateTime?: Date,
  todate?: Date,
  startTime?: string,
  endTime?: string,
): {
  modifiedDate?: Date;
  modifiedToDate?: Date;
} => {
  if (startTime) {
    const [hour, minute] = is24HoursFormat ? startTime.split(':') : timeFormat12to24Converter(startTime);
    dateTime?.setHours(Number(hour));
    dateTime?.setMinutes(Number(minute));
  }
  if (endTime) {
    const [hour, minute] = is24HoursFormat ? endTime.split(':') : timeFormat12to24Converter(endTime);
    todate?.setHours(Number(hour));
    todate?.setMinutes(Number(minute));
  }

  return { ...{ ...optionalProp('modifiedDate', dateTime) }, ...{ ...optionalProp('modifiedToDate', todate) } };
};
// Time Related Helper methods
const MAX_TIME_INPUT_LENGTH = 2;
const MAX_HOURS_IN_12_HOUR_FORMAT = 12;
/**
 *
 * @param time to compare with start time
 * @returns true if startTime is greater or equal to time.
 */
export const checkIsValidEndTime = (time: string, startTime?: string, is24HoursFormat?: boolean): boolean => {
  if (startTime) {
    const [startTimeHour, startTimeMin] = startTime.split(':');
    const [startTimeMinute, startTimeMeridium] = startTimeMin?.split(' ') as any;
    const [currentEndHour, currentEndMin] = time.split(':');
    const [currentEndMinute, currentEndMeridium] = currentEndMin?.split(' ') as any;

    if (is24HoursFormat) {
      if (startTimeMeridium === TimeMeridiem.AM) {
        if (currentEndMeridium === TimeMeridiem.PM) {
          return false;
        }
      }
    }
    if (Number(currentEndHour) < Number(startTimeHour)) {
      return true;
    } else if (
      Number(currentEndHour) === Number(startTimeHour) &&
      Number(startTimeMinute) <= Number(currentEndMinute)
    ) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

export const gettimeIn12HourFormat = (hour: string, minute: string): string => {
  const [currentMinute, meridium] = minute.split(' ');
  const currentMeridiem = meridium || (Number(hour) < MAX_HOURS_IN_12_HOUR_FORMAT ? TimeMeridiem.AM : TimeMeridiem.PM);
  let formatterHour = hour;
  if (Number(hour) === 0) {
    formatterHour = MAX_HOURS_IN_12_HOUR_FORMAT.toString();
  } else if (Number(hour) > 12) {
    formatterHour = (Number(hour) - 12).toString().padStart(MAX_TIME_INPUT_LENGTH, '0');
  }
  return `${formatterHour}:${currentMinute} ${currentMeridiem}`;
};

export const getTimeIn24HourFormat = (hour: string, minute: string): string => {
  const currentHour = hour.padStart(2, '0'),
    currentMin = minute.padStart(2, '0');

  return `${currentHour}:${currentMin}`;
};

export const timeInFormat = (is24HoursFormat: boolean, time?: string) => {
  if (time) {
    const [hour, minute] = time.split(':') as any;
    const formatter = is24HoursFormat ? getTimeIn24HourFormat : gettimeIn12HourFormat;
    return formatter(hour, minute);
  }
  return time;
};

export const getCeilingTime = (is24HoursFormat: boolean, time?: string) => {
  if (time) {
    const [hour, minute] = time.split(':');
    let currentHour = hour;
    const [interimMinute, meridium] = minute?.split(' ') as any;
    let currentMinute = interimMinute;
    if (currentMinute !== '00') {
      if (60 - Number(currentMinute) < 30) {
        currentHour = String(Number(currentHour) + 1);
      }
      currentMinute = `00${meridium ? ` ${meridium}` : ''}`;
    }
    return timeInFormat(
      is24HoursFormat,
      `${currentHour?.padStart(2, '0')}:${currentMinute}${meridium ? ` ${meridium}` : ''}`,
    );
  }
  return undefined;
};
export const timeFormat12to24Converter = (time: string) => {
  const [hour, interimMinute] = time.split(':');
  const [minute, meridiem] = interimMinute?.split(' ') as any;
  let currentHour = Number(hour);
  const currentMinute = Number(minute);
  if (meridiem === TimeMeridiem.PM) {
    currentHour += MAX_HOURS_IN_12_HOUR_FORMAT;
  }
  return [currentHour, currentMinute];
};
export const getYearList = () => {
  const firstYear = new Date().getFullYear();
  const initialLimit = firstYear;
  const limit = firstYear + 10;
  const years: { id: string; name: any }[] = [];
  for (let i = initialLimit; i < limit; i++) {
    years.push({ id: String(i), name: i });
  }
  return years;
};
export const renderMonthOrYearSelectorBody = (
  setCalendarInterimDateTime: React.Dispatch<React.SetStateAction<Date | undefined>>,
  selectorName: string,
  month: string[],
  currentMonth: number,
  year: Data[],
  currentYear: number,
  onSelectorClick: (selector: SelectorType) => void,
  autoCloseMonthYearSelector: boolean,
  handleDateTimeUpdate?: (
    event: ReactMouseEvent<HTMLButtonElement, MouseEvent>,
    currentDate: Date,
    isMouseEvent: boolean,
  ) => void,
  handleDisableMonthCB?: (month: string) => boolean,
  disableYear?: (year: number) => boolean,
) => {
  const monthBody = month.map((m, index) => (
    <button
      type="button"
      key={m}
      className={`${styles['month-selector']} ${index === currentMonth ? styles['active'] : ''}`}
      onClick={(e) => {
        handleDateTimeUpdate?.(e, new Date(currentYear, index), false);
        setCalendarInterimDateTime(new Date(currentYear, index));
        if (autoCloseMonthYearSelector) {
          onSelectorClick(SelectorType.MONTH);
        }
      }}
      disabled={handleDisableMonthCB?.(m)}
    >
      {m.substring(0, 3)}
    </button>
  ));
  const yearBody = year.map((y) => (
    <button
      type="button"
      key={`${y.id}`}
      className={`${styles['year-selector']} ${y.name === currentYear ? styles['active'] : ''}`}
      onClick={(e) => {
        handleDateTimeUpdate?.(e, new Date(y.id as number, currentMonth), false);
        setCalendarInterimDateTime(new Date(y.id as number, currentMonth));
        if (autoCloseMonthYearSelector) {
          onSelectorClick(SelectorType.YEAR);
        }
      }}
      disabled={disableYear?.(y.name as number)}
    >
      {y.name as string}
    </button>
  ));
  const selectorBody = selectorName === SelectorType.MONTH ? monthBody : yearBody;
  return <section className={styles['selector-body']}>{selectorBody}</section>;
};
